import {Application} from '../types';
import {ApplicationInfrastructure} from '../types-infrastructure';

// FNP-1601: THE ONE PLACE THE CONSOLE DECIDES WHAT A TEARDOWN WOULD DESTROY.
//
// ⚠️ THIS MUST TRACK console-api's
// `src/lambda/create-application-handler/src/operations/validate-deprovisionable.ts`.
// That file is authoritative; this is the client-side affordance over it. When
// the ladder there changes, change it here.
//
// ── WHY A SEAM AND NOT THREE COPIES ─────────────────────────────────────────
//
// Before this ticket the console derived teardown targets in THREE places, all
// reading `Application.infraStackNames` and none reading the inventory:
//
//   - `getDecommissionEligibility`  — chose the refusal and the remedy
//   - `hasNeverBeenProvisioned`     — chose between two remedies, one of which
//                                     is "delete this record instead"
//   - `DecommissionButton`          — built the stack list shown IN THE
//                                     DESTRUCTIVE CONFIRMATION MODAL
//
// Each carried a comment claiming it mirrored the server. A mirror is only
// correct while the thing it mirrors holds still, and the server had moved:
// FNP-1016 made `ApplicationInfrastructure` authoritative and pointed the
// server's reader at it. The console never followed.
//
// The consequence was not cosmetic. On an application provisioned TODAY —
// inventory row, no legacy fields — the confirmation modal showed the operator
// an EMPTY LIST OF STACKS while the server proceeded to destroy the
// inventory-derived ones. A destructive confirmation that understates what it
// will destroy is worse than no confirmation, because it is read as assurance.
//
// One seam, consumed by all three, so the next divergence is a one-file fix.

export interface ConsoleTeardownTarget {
    stage: 'dev' | 'test' | 'prod';
    stackName: string;
    bucketName?: string;
}

export type TeardownTargetSource = 'inventory' | 'legacy';

export interface ConsoleTeardownView {
    targets: ConsoleTeardownTarget[];
    // Which representation supplied them. Mirrors `DeprovisionVerdict.source`.
    source: TeardownTargetSource;
}

const ALL_STAGES: Array<'dev' | 'test' | 'prod'> = ['dev', 'test', 'prod'];

// The INVENTORY's targets — sparse by design.
//
// ⚠️ NO EMPTY-STRING FILTERING IS NEEDED, and that is the payoff of the sparse
// array: a stage that was never provisioned is simply absent. A stage entry
// with a blank stack name is still skipped, because a target with no stack
// name is not a target.
function inventoryTargets(
    infrastructure: ApplicationInfrastructure | null | undefined,
): ConsoleTeardownTarget[] {
    const stages = infrastructure?.stages;
    if (!stages) return [];
    const targets: ConsoleTeardownTarget[] = [];
    for (const stage of ALL_STAGES) {
        const entry = stages.filter((s) => s.stage === stage)[0];
        if (!entry || !entry.infraStackName) continue;
        targets.push({
            stage,
            stackName: entry.infraStackName,
            bucketName: entry.deploymentBucketName ?? '',
        });
    }
    return targets;
}

// The LEGACY targets, off the denormalised `Application` map.
//
// Empty string means "this orchestrator does not deploy that stage":
// `StageStringMap` declares all three keys non-null, so absence has to be
// encoded rather than omitted, and must be filtered rather than deleted.
function legacyTargets(record: Application): ConsoleTeardownTarget[] {
    const stackNames = record.infraStackNames;
    if (!stackNames) return [];
    const buckets = record.deploymentBucketNames;
    const targets: ConsoleTeardownTarget[] = [];
    for (const stage of ALL_STAGES) {
        const stackName = stackNames[stage] ?? '';
        if (!stackName) continue;
        targets.push({stage, stackName, bucketName: buckets?.[stage] ?? ''});
    }
    return targets;
}

// What a teardown of this application would destroy, and which record says so.
//
// ⚠️ PREFER THE INVENTORY; FALL BACK TO THE `Application` FIELDS. The fallback
// is NOT legacy cruft to be removed — 7 of 9 production applications have no
// inventory row at all, so removing it would blind the console for the dominant
// population. It goes away when those records are registered, not before.
//
// ⚠️ ONE TARGET, ONE REPRESENTATION. The branch is chosen ONCE, per record, and
// every field of every target then comes from that one source. The two are
// never joined within a target — that join is the defect this ticket closed on
// the server, and reproducing it here would put it straight back.
export function teardownTargetsFor(
    record: Application,
    infrastructure?: ApplicationInfrastructure | null,
): ConsoleTeardownView {
    const fromInventory = inventoryTargets(infrastructure);
    if (fromInventory.length > 0) {
        return {targets: fromInventory, source: 'inventory'};
    }
    return {targets: legacyTargets(record), source: 'legacy'};
}

// Does this record own infrastructure according to EITHER representation?
//
// ⚠️ DELIBERATELY THE WIDER TEST. Used to decide whether a record can be called
// "never provisioned" — a label whose remedy is "delete this record instead".
// Getting that wrong in the permissive direction tells an operator to delete a
// record pointing at live infrastructure, so every signal from both records
// must be absent before the label is allowed.
export function hasAnyRecordedInfrastructure(
    record: Application,
    infrastructure?: ApplicationInfrastructure | null,
): boolean {
    if (teardownTargetsFor(record, infrastructure).targets.length > 0) return true;

    // An inventory row that exists at all is evidence, even before it names a
    // stack: the write-before-create rule means a row may be written PENDING,
    // ahead of the resource it describes. Treating that as "nothing here" is
    // the silent-orphan failure that rule exists to prevent.
    if (infrastructure) return true;

    return Boolean(
        record.sourceRepositoryName
        || record.infraRepositoryName
        || record.sourcePipelineId
        || record.infraPipelineId
        || record.provisioningExecutionId,
    );
}
